import * as log from 'loglevel';
import {html} from 'lit-html';
import {repeat} from 'lit-html/directives/repeat';
import {classMap} from 'lit-html/directives/class-map';
import {unsafeHTML} from 'lit-html/directives/unsafe-html';

import TemplateRegistry from '../template/template-registry';
import {optionsTemplate} from '../template/product-options.template.js';

import './tabs.option.css';
import {styleMap} from 'lit-html/directives/style-map';
import Translate from '../translate';

class TabsController {

    constructor(onChange, getOption) {
        this.onChange = onChange;
        this.getOption = getOption;
        this.config = null; // reference updated on each render
        this.option = null; // reference updated on each render
        this.activeTab = 0; // active tab index
    }

    initTabs(){

        for (let index = 0; index < this.option.options.length; index++) {
            const optionTab = this.option.options[index];

            // move label to tab and hide on option level
            if(optionTab.label){
                optionTab._tabLabel = optionTab.label;
                optionTab.label = '';
            }
        }

        // editorMode: de-register previous event handler
        if (this.optionsValidated) {
            this.config.productForm.removeEventListener('options:validated', this.optionsValidated);
        }

        this.optionsValidated = this.onOptionsValidated.bind(this);

        // listen to options validations
        this.config.productForm.addEventListener('options:validated', this.optionsValidated);
    }

    onOptionsValidated(event) {
        // check valid state of all options
        if(!event.detail.valid){
            // select first invalid tab
            let tabIndex = this.findInvalidTabIndex(this.option.options);

            if(tabIndex !== -1){
                // next validation triggered for pending tab > clear only this tab
                if(tabIndex >= this.pendingTab){ // prefer pending tab if less or equal to invalid tab
                    tabIndex = this.pendingTab;
                    log.debug('tabs: clearing errors on pending tab', tabIndex);
                    this.clearTabErrors([this.option.options[tabIndex]]);
                    delete this.pendingTab;
                }
                // tab base validation > clear other tabs (required to avoid showing error animation when tab is changed)
                else if(this.option.params.tabsValidation){
                    log.debug('tabs: clearing errors on other tabs except', tabIndex);
                    this.clearTabErrors(this.option.options, tabIndex);
                }

                this.setActiveTab(tabIndex);
            }
        }
        // navigate to pending tab if any
        else if(this.pendingTab !== undefined){
            this.setActiveTab(this.pendingTab);
            delete this.pendingTab;
        }

        // render is called after each validation
    }

    // NEW: tabsViewSwitch - option:tabs:changed tells the gallery which tab became active (only on a real index change)
    setActiveTab(index) {
        if (index === this.activeTab) return;

        this.activeTab = index;

        if (this.option.params.tabsViewSwitch) {
            this.config.productForm.dispatchEvent(new CustomEvent('option:tabs:changed', {
                detail: { option: this.option, tab: this.option.options[index], index },
                bubbles: true
            }));
        }
    }

    findInvalidTabIndex(options, tabIndex = -1){
        for (let i = 0; i < options.length; i++) {
            const option = options[i];
            if(option.errors?.length){
                return tabIndex === -1 ? i : tabIndex;
            }
            // nested options
            if(option.options?.length){
                // pass tab index
                let nestedIndex = this.findInvalidTabIndex(option.options, i);
                if(nestedIndex !== -1){
                    return nestedIndex;
                }
            }
        }
        return -1;
    }

    clearTabErrors(options, excludeIndex = -1){
        for (let i = 0; i < options.length; i++) {
            if(i === excludeIndex) continue;
            const option = options[i];
            if (option.errors?.length) {
                delete option.errors;
            }
            // nested options
            if (option.options?.length) {
                this.clearTabErrors(option.options);
            }
        }
    }

    renderOptions() {
        this.config.productForm.dispatchEvent(new CustomEvent('options:render', { 
            detail: this.option, 
            cancelable: true, 
            bubbles: true 
        }));
    }

    validateOptions() {
        this.config.productForm.dispatchEvent(new CustomEvent('options:validate', {
            detail: this.option,
            cancelable: true,
            bubbles: true
        }));
    }

    selectTab(index) {
        this.setActiveTab(index);
        // Re-render to show the new active tab
        this.renderOptions();
    }

    switchTab(index) {
        // validate only when moving forward
        if (this.option.params.tabsValidation && index >= this.activeTab) {
            this.pendingTab = index;
            this.validateOptions();
        } else {
            this.selectTab(index);
        }
    }

    nextTab() {
        if (this.option.params.tabsValidation) {
            // store next tab to activate after validation passed
            this.pendingTab = this.activeTab + 1;
            this.validateOptions();
        } else {
            this.selectTab(this.activeTab + 1);
        }
    }

    prevTab() {
        this.selectTab(this.activeTab - 1);
    }

    tabsStyleMap(styleMap = {}) {
        const { tabsButtonColor, tabsButtonLabelColor, tabsButtonAlignment, tabsLayout } = this.option.params || {};

        if (tabsButtonColor) styleMap['--tabs-button-color'] = tabsButtonColor;
        if (tabsButtonLabelColor) styleMap['--tabs-button-label-color'] = tabsButtonLabelColor;
        if (tabsButtonAlignment) styleMap['--tabs-button-alignment'] = tabsButtonAlignment === 'space' ? 'space-between' : 'flex-end';
        if (tabsLayout) styleMap['--tabs-layout'] = tabsLayout === 'tabs' ? 'tabs' : 'grid';

        return styleMap;
    }

}   

export default TabsController;

TemplateRegistry.templates['tabs'] = (option, config, onChange, onErrorClear, getOption) => {

    let init = option._controller === undefined;

    // Get or create Tabs controller
    option._controller = option._controller || new TabsController(onChange, getOption);

    // Update references
    option._controller.option = option;
    option._controller.config = config;

    if(init){
        option._controller.initTabs();
    }
    
    const controller = option._controller;

    const prevLabel = Translate.get('product.options.tabs.previous', null, 'Previous');
    const nextLabel = Translate.get('product.options.tabs.next', null, 'Next');
    const tabsList = option.params?.tabsList ?? true;

    return html`
        <div class="cl-po--tabs-container" style=${styleMap(controller.tabsStyleMap())} data-tabs-layout=${option.params?.tabsLayout || 'tabs'}>
            ${option.options && option.options.length > 0 ? html`

                ${tabsList ? html`<div class="cl-po--tabs-nav" role="tablist" aria-label=${option.label || option.name || 'Tab'}>
                    ${repeat(option.options, (tab, index) => index, (tab, index) => html`
                        <button
                            type="button"
                            class="cl-po--tab-item ${classMap({'cl-po--tab-active': controller.activeTab === index})}"
                            id=${`cl-po-tab-${index}`}
                            role="tab"
                            aria-selected=${controller.activeTab === index}
                            aria-controls=${`cl-po-tab-panel-${index}`}
                            tabindex=${controller.activeTab === index ? '0' : '-1'}
                            @click=${() => controller.switchTab(index)}
                            title=${tab._tabLabel || tab.name || `Tab ${index + 1}`}
                        >
                            <span class="cl-po--tab-item-text">${unsafeHTML(tab._tabLabel || tab.name || `Tab ${index + 1}`)}</span>
                        </button>
                    `)}
                </div>` : null}
                
                <div class="cl-po--tab-content">
                    ${repeat(option.options, (tab, index) => index, (tab, index) => html`
                        <div class="cl-po--tab-panel ${classMap({'cl-po--tab-panel-active': controller.activeTab === index})}" id=${`cl-po-tab-panel-${index}`} role="tabpanel" aria-labelledby=${`cl-po-tab-${index}`} ?hidden=${controller.activeTab !== index}>
                            ${optionsTemplate([tab], config, onChange, onErrorClear, getOption)}
                        </div>
                    `)}
                    ${option.params?.tabsNavigation ? html`
                    <div class="cl-po--tabs-button">
                        <button class="cl-po--btn-prev" type="button" @click=${() => controller.prevTab()} 
                        ?hidden=${controller.activeTab === 0}>${prevLabel}</button>

                        <button class="cl-po--btn-next" type="button" @click=${() => controller.nextTab()} 
                        ?hidden=${controller.activeTab === option.options.length - 1}>${nextLabel}</button>
                    </div>` : null}
                </div>
            ` : null}
        </div>
    `;
};
